/**
 * Dialogfenster.
 *
 * Bis hierher stand dasselbe Markup zweimal im Code — im Buchungsdialog und in
 * den Belegungsdetails. Das Muster ist unauffällig genug, dass eine dritte Kopie
 * nicht weiter aufgefallen wäre, und heikel genug, dass sie es sollte: Ohne das
 * `stopPropagation` schließt der Dialog beim Klick auf sein eigenes Formular.
 */
export function Modal({
  title,
  description,
  onClose,
  size = 'md',
  children,
}: {
  title: string;
  description?: string;
  onClose: () => void;
  size?: 'sm' | 'md' | 'lg';
  children: React.ReactNode;
}) {
  const width = { sm: 'max-w-sm', md: 'max-w-lg', lg: 'max-w-3xl' }[size];

  return (
    /**
     * Zentriert wird über `m-auto` am Dialog, nicht über `items-center` am
     * Container.
     *
     * Das ist kein Geschmacksunterschied: Ein zentriertes Flex-Kind, das höher
     * ist als der scrollbare Container, wächst über dessen **obere** Kante
     * hinaus — und dorthin lässt sich nicht scrollen. Der Anfang des Formulars
     * wäre dann unerreichbar abgeschnitten. Automatische Ränder zentrieren
     * dagegen nur den überschüssigen Platz und verschwinden, sobald keiner mehr
     * da ist.
     */
    <div
      className="fixed inset-0 z-50 flex justify-center overflow-y-auto bg-slate-900/40 p-4"
      onClick={onClose}
      role="presentation"
    >
      <div
        className={`m-auto flex max-h-[calc(100vh-2rem)] w-full flex-col rounded-lg bg-white shadow-xl ${width}`}
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        {/* Überschrift bleibt stehen — bei einem langen Formular sonst als
            Erstes weggescrollt, und man weiß nicht mehr, was man bearbeitet. */}
        <div className="shrink-0 border-b border-slate-200 px-6 py-4">
          <h2 className="text-base font-semibold">{title}</h2>
          {description && <p className="mt-1 text-sm text-slate-500">{description}</p>}
        </div>
        <div className="overflow-y-auto px-6 py-5">{children}</div>
      </div>
    </div>
  );
}
